import type { BlogPostDraft } from '../../lib/blog';

export const post: BlogPostDraft = {
  slug: 'relancer-marque-ugc',
  lang: 'fr',
  title: 'Comment relancer une marque en UGC sans paraître insistant·e ?',
  description:
    'Découvre quand relancer une marque en UGC, combien de messages envoyer et comment reprendre contact sans paraître insistant·e.',
  date: '2026-07-29',
  author: 'Équipe Leadsme',
  tags: ['prospection', 'relance', 'ugc'],
  ctaTitleKey: 'blog.postCtaTitle',
  ctaDescriptionKey: 'blog.postCtaDescription',
  ctaButtonKey: 'blog.postCtaButton',
  content: `
<p class="blog-lead">
  Envoyer un email de prospection ne garantit pas une réponse immédiate. Ton message peut arriver au mauvais moment, être transféré à une autre personne ou simplement se perdre dans une boîte de réception chargée.
</p>
<p>
  Relancer ne signifie pas mettre la pression à la marque. Une bonne relance permet surtout de remettre ta proposition sous les yeux de ton interlocuteur, au bon moment et avec un message clair.
</p>
<p>
  Tu n’as pas encore envoyé ton premier message ? Découvre nos <a href="/blog/email-prospection-ugc">7 exemples d’emails de prospection UGC</a> pour contacter les marques.
</p>

<h2>Quand relancer une marque ?</h2>
<p>
  Après un premier email de prospection, attends généralement 5 à 7 jours ouvrés avant d’envoyer une relance.
</p>
<p>
  Ce délai laisse le temps à la marque de consulter ton message, ton portfolio ou de transmettre ta proposition à la bonne personne. Relancer dès le lendemain peut sembler insistant, tandis qu’attendre plusieurs semaines risque de faire perdre le contexte de ton premier email.
</p>
<p>
  Le délai peut toutefois varier selon la situation. Si ta proposition concerne une campagne saisonnière, un lancement ou une date précise, adapte ton calendrier pour que ton idée reste pertinente.
</p>
<p>
  Après l’envoi d’un devis ou de tes tarifs, tu peux également attendre environ une semaine avant de reprendre contact.
</p>

<h2>Combien de fois faut-il relancer ?</h2>
<p>Dans la plupart des cas, deux à trois relances maximum suffisent :</p>
<ul>
  <li>une première relance après 5 à 7 jours ouvrés</li>
  <li>une deuxième environ une semaine plus tard</li>
  <li>éventuellement un dernier message pour clôturer l’échange</li>
</ul>
<p>
  Sans réponse après plusieurs tentatives, mieux vaut passer à une autre opportunité. Tu pourras toujours recontacter la marque quelques mois plus tard avec une nouvelle idée, une actualité pertinente ou un contenu mieux adapté.
</p>
<p>
  L’objectif n’est pas de relancer le plus souvent possible, mais de le faire au bon moment.
</p>

<h2>Que doit contenir une bonne relance ?</h2>
<p>
  Une relance doit rester courte. Ton interlocuteur doit rapidement comprendre pourquoi tu le contactes et quelle réponse tu attends.
</p>
<p>Tu peux suivre cette structure :</p>
<ul>
  <li>rappeler brièvement ton précédent message</li>
  <li>préciser ce que tu proposes à la marque</li>
  <li>ajouter une nouvelle information si elle est pertinente</li>
  <li>terminer par une question simple</li>
</ul>
<p>
  Évite de recopier l’intégralité de ton premier email. Réponds directement dans le même fil de discussion afin que la marque puisse facilement retrouver le contexte.
</p>
<p>
  Une relance peut aussi apporter une nouvelle raison de répondre. Une idée de vidéo, une réalisation récente dans le même secteur, une disponibilité ou un exemple de contenu adapté à la communication de la marque.
</p>
<p>
  Il n’est cependant pas nécessaire d’inventer quelque chose à chaque message. Une relance courte et personnalisée reste préférable à une idée peu pertinente.
</p>

<h2>Exemple 1 : relancer après une prospection sans réponse</h2>
<blockquote>
  <p>
    Bonjour [Prénom],<br/><br/>
    Je me permets de revenir vers vous concernant ma proposition de contenu UGC envoyée il y a quelques jours.<br/><br/>
    Je serais toujours ravi·e d’échanger sur vos éventuels besoins et de vous proposer quelques idées adaptées à [Marque].<br/><br/>
    Je vous remets mon portfolio ici : [lien]<br/><br/>
    Est-ce un sujet d’actualité pour votre équipe en ce moment ?<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Ce message rappelle le contexte, facilite l’accès au portfolio et se termine par une question claire.
</p>
<p>
  Évite les formulations comme « Je n’ai toujours pas reçu de réponse ». L’absence de réponse n’est pas nécessairement un refus.
</p>

<h2>Exemple 2 : relancer après l’envoi d’un devis ou de ses tarifs</h2>
<p>
  Lorsque la marque t’a déjà répondu, inutile de présenter à nouveau ton profil. L’objectif est plutôt de savoir si le projet avance et si elle a besoin d’une précision.
</p>
<blockquote>
  <p>
    Bonjour [Prénom],<br/><br/>
    Je me permets de revenir vers vous concernant la proposition envoyée pour [nom du projet].<br/><br/>
    Avez-vous eu l’occasion de la consulter ? Je reste disponible si vous souhaitez ajuster certains éléments ou obtenir une précision sur les prestations proposées.<br/><br/>
    Le projet est-il toujours prévu de votre côté ?<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Ne propose pas automatiquement une réduction parce que la marque tarde à répondre. Le silence peut venir d’une validation interne, d’un budget en attente ou d’un changement de calendrier.
</p>

<h2>Exemple 3 : envoyer une dernière relance</h2>
<p>
  Après deux ou trois messages sans réponse, tu peux clôturer l’échange de manière professionnelle tout en laissant la porte ouverte.
</p>
<blockquote>
  <p>
    Bonjour [Prénom],<br/><br/>
    Je me permets de vous envoyer un dernier message concernant ma proposition de contenu UGC.<br/><br/>
    Je comprends que le sujet ne soit peut-être pas d’actualité pour le moment. Je reste disponible si vos besoins évoluent dans les prochains mois.<br/><br/>
    Bonne journée,<br/>
    [Prénom]
  </p>
</blockquote>
<p>
  Cette dernière relance ne cherche pas à provoquer une réponse à tout prix. Elle permet simplement de terminer l’échange proprement.
</p>

<h2>Les erreurs à éviter</h2>
<p>Pour ne pas paraître insistant·e, évite de :</p>
<ul>
  <li>relancer dès le lendemain</li>
  <li>envoyer exactement le même message plusieurs fois</li>
  <li>culpabiliser la marque pour son absence de réponse</li>
  <li>écrire un email beaucoup plus long que ta proposition initiale</li>
  <li>contacter la marque par email, Instagram et LinkedIn le même jour</li>
  <li>proposer une réduction sans connaître le véritable point de blocage</li>
</ul>
<p>
  Pense aussi à vérifier que ta proposition est toujours pertinente. Une idée liée à Noël, à la rentrée ou à un lancement ne doit pas être relancée plusieurs semaines après la période concernée.
</p>

<h2>Comment suivre ses relances ?</h2>
<p>
  Lorsque tu contactes plusieurs marques, il devient vite difficile de te souvenir de chaque échange.
</p>
<p>Pour chaque prospect, conserve au minimum :</p>
<ul>
  <li>la date du premier contact</li>
  <li>le nom de ton interlocuteur</li>
  <li>la date de la prochaine relance</li>
  <li>le statut de la proposition</li>
  <li>les informations importantes sur l’échange</li>
</ul>
<p>
  Un tableau peut suffire au début. Mais lorsque les contacts, les devis et les projets se multiplient, centraliser ces informations permet de gagner du temps et d’éviter les oublis.
</p>
<p>
  Avec Leadsme, tu peux organiser tes prospects, suivre l’avancement de tes échanges et savoir quelles marques relancer sans parcourir toute ta boîte mail.
</p>
<p>
  <a href="/signup">Créer gratuitement mon espace Leadsme</a>
</p>

<h2>Pour résumer</h2>
<p>
  Attends généralement 5 à 7 jours ouvrés avant ta première relance et limite-toi à deux ou trois messages.
</p>
<p>
  Garde ta relance courte, rappelle le contexte et termine par une question simple. Lorsque c’est pertinent, ajoute une nouvelle information qui donne à la marque une raison de reprendre la discussion.
</p>
<p>
  Sans réponse, clôture l’échange proprement et concentre-toi sur d’autres opportunités. Tu pourras toujours revenir plus tard avec une proposition réellement nouvelle.
</p>
`,
};
